'use client';

import {
  ForwardedRef,
  forwardRef,
  HtmlHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import './css/Menu.scss';

export type MenuPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';
export type MenuTriggerStyle = 'default' | 'high-contrast' | 'reversed';

export interface MenuProps extends HtmlHTMLAttributes<HTMLElement> {
  autoPosition?: boolean;
  buttonAriaLabel: string;
  buttonStyle?: MenuTriggerStyle;
  buttonText?: string;
  isDisabled?: boolean;
  isExpanded?: boolean;
  position?: MenuPosition;
}

export const Menu = forwardRef(
  (props: MenuProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      autoPosition = false,
      buttonAriaLabel,
      buttonStyle = 'default',
      buttonText = '',
      isDisabled = false,
      isExpanded: isMenuExpanded = false,
      position: menuPosition = 'bottom-right',
      children,
      className,
      ...spreadProps
    } = props;

    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);

    const uniqueMenuId = `${useId()}-menu`;
    const uniqueMenuTriggerId = `${useId()}-menu-trigger`;

    const [isExpanded, setIsExpanded] = useState<boolean>(isMenuExpanded);
    const [menuItems, setMenuItems] = useState<Array<HTMLElement>>([]);
    const [position, setPosition] = useState<MenuPosition>(menuPosition);

    /**
     * Sets the tabindex for the next menu item to receive focus to 0 and all others
     * to -1.
     */
    const manageRovingTabindex = useCallback(
      (index: number) => {
        menuItems.forEach((menuItem, i) =>
          i === index
            ? menuItem.setAttribute('tabindex', '0')
            : menuItem.setAttribute('tabindex', '-1')
        );
      },
      [menuItems]
    );

    const closeMenu = useCallback(() => {
      setIsExpanded(false);
      /* Timeout required to ensure menu trigger receives focus on Shift + Tab, not
      the first menu item. */
      setTimeout(() => manageRovingTabindex(0), 0);
    }, [manageRovingTabindex]);

    const openMenu = () => {
      setIsExpanded(true);
      // Timeout required to ensure menu container has rendered.
      setTimeout(() => focusMenuItem(0), 15);
    };

    const toggleMenu = () => {
      if (isExpanded) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    const focusMenuItem = (index: number) => {
      manageRovingTabindex(index);
      menuItems[index]?.focus();
    };

    const focusNextMenuItem = () => {
      const currentIndex = menuItems.indexOf(
        document.activeElement as HTMLElement
      );

      if (currentIndex >= 0) {
        if (currentIndex === menuItems.length - 1) {
          focusMenuItem(0);
        } else {
          focusMenuItem(currentIndex + 1);
        }
      }
    };

    const focusPreviousMenuItem = () => {
      const currentIndex = menuItems.indexOf(
        document.activeElement as HTMLElement
      );

      if (currentIndex >= 0) {
        if (currentIndex === 0) {
          focusMenuItem(menuItems.length - 1);
        } else {
          focusMenuItem(currentIndex - 1);
        }
      }
    };

    const focusMenuTrigger = () => buttonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      const elem = e.target as HTMLElement;

      switch (e.key) {
        case 'Tab':
          if (elem.role === 'menuitem') {
            closeMenu();
          }
          break;
        case 'Escape':
          closeMenu();
          focusMenuTrigger();
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          if (isExpanded) {
            e.preventDefault();
          }
          break;
        case 'ArrowDown':
          if (isExpanded) {
            e.preventDefault();
            focusNextMenuItem();
          }
          break;
        case 'ArrowUp':
          if (isExpanded) {
            e.preventDefault();
            focusPreviousMenuItem();
          }
          break;
        case 'Home':
          if (isExpanded) {
            focusMenuItem(0);
          }
          break;
        case 'End':
          if (isExpanded) {
            focusMenuItem(menuItems.length - 1);
          }
          break;
        default:
          break;
      }
    };

    const handleMenuItemSelection = (e: MouseEvent) => {
      const elem = e.target as HTMLElement;
      const isMenuItem = elem.getAttribute('role') === 'menuitem';
      const isAriaDisabled = elem.getAttribute('aria-disabled') === 'true';

      if (isMenuItem && !isAriaDisabled) {
        closeMenu();
      }
    };

    const setOverlayPosition = useCallback(
      (position: MenuPosition) => {
        if (!buttonRef.current) return;
        const buttonHeight = buttonRef.current.offsetHeight;
        const buttonOffsetLeft = buttonRef.current.offsetLeft;
        const buttonOffsetTop = buttonRef.current.offsetTop;

        let newPositionTop: string = 'auto';
        let newPositionBottom: string = 'auto';
        let newPositionLeft: string = 'auto';
        let newPositionRight: string = 'auto';

        switch (position) {
          case 'bottom-right':
            newPositionTop = `${buttonOffsetTop + buttonHeight}px`;
            newPositionLeft = `${buttonOffsetLeft}px`;
            break;
          case 'bottom-left':
            newPositionTop = `${buttonOffsetTop + buttonHeight}px`;
            newPositionRight = `${buttonOffsetLeft}px`;
            break;
          case 'top-right':
            newPositionBottom = `calc(100% - ${buttonOffsetTop}px)`;
            newPositionLeft = `${buttonOffsetLeft}px`;
            break;
          case 'top-left':
            newPositionBottom = `calc(100% - ${buttonOffsetTop}px)`;
            newPositionRight = `${buttonOffsetLeft}px`;
            break;
          default:
            break;
        }

        menuContainerRef.current!.style.top = newPositionTop;
        menuContainerRef.current!.style.bottom = newPositionBottom;
        menuContainerRef.current!.style.left = newPositionLeft;
        menuContainerRef.current!.style.right = newPositionRight;
      },
      [buttonRef]
    );

    useEffect(() => manageRovingTabindex(0), [manageRovingTabindex, menuItems]);

    useEffect(() => {
      const menuItemsNodes =
        menuRef.current?.querySelectorAll('[role="menuitem"]');
      const items = Array.from(menuItemsNodes!) as Array<HTMLElement>;

      if (items.length) {
        setMenuItems(items);
      }
    }, [children]);

    useEffect(() => {
      if (buttonAriaLabel.length === 0) {
        console.warn(
          "You must provide a value for the Pulse Menu component's buttonAriaLabel."
        );
      }

      if (
        buttonText.length > 0 &&
        buttonText !== 'Menu' &&
        buttonText !== 'More'
      ) {
        console.warn(
          'Pulse Menu button text should be one of "Menu" or "More". Refer to the Menu content guidelines for additional information.'
        );
      }
    }, [buttonAriaLabel, buttonText]);

    useEffect(() => {
      const handleOutsideClick = (e: Event) => {
        const elem = e.target as HTMLElement;
        const isInsideClick = menuRef.current?.contains(elem);
        const isTriggerClick = buttonRef.current?.contains(elem);

        if (!isInsideClick && !isTriggerClick) {
          closeMenu();
        }
      };
      window.addEventListener('click', handleOutsideClick);

      return () => {
        window.removeEventListener('click', handleOutsideClick);
      };
    }, [closeMenu]);

    useEffect(() => setIsExpanded(isMenuExpanded), [isMenuExpanded]);

    useEffect(() => setPosition(menuPosition), [menuPosition]);

    useEffect(
      () => setOverlayPosition(position),
      [position, setOverlayPosition]
    );

    useEffect(() => {
      const getOptimalMenuPosition = () => {
        const domRect = buttonRef.current?.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        if (!domRect) return;

        /* If the menu trigger is below the center of the viewport, place the menu above. */
        const getVerticalPosition = (
          y: number,
          elemHeight: number,
          winHeight: number
        ) => (y + elemHeight / 2 > winHeight / 2 ? 'top' : 'bottom');

        /* If the menu trigger is positioned right of center, place the menu to the left. */
        const getHorizontalPosition = (
          x: number,
          elemWidth: number,
          winWidth: number
        ) => (x + elemWidth / 2 > winWidth / 2 ? 'left' : 'right');

        const newPosition: MenuPosition = `${getVerticalPosition(
          domRect.y,
          domRect.height,
          windowHeight
        )}-${getHorizontalPosition(domRect.x, domRect.width, windowWidth)}`;

        return newPosition;
      };

      const positionMenu = () => {
        if (autoPosition) {
          const newPosition = getOptimalMenuPosition() ?? position;
          setPosition(newPosition);
          setOverlayPosition(newPosition);
        } else {
          setOverlayPosition(position);
        }
      };
      window.addEventListener('resize', positionMenu);

      return () => {
        window.removeEventListener('resize', positionMenu);
      };
    }, [autoPosition, position, setOverlayPosition]);

    const buttonClasses = classNames({
      'ps-menu-trigger': true,
      'ps-menu-trigger-icon-button': !buttonText,
      [`ps-menu-trigger-${buttonStyle}`]: buttonStyle !== 'default',
    });

    const menuClasses = classNames({
      'ps-menu': true,
      [`${className}`]: className,
    });

    const menuListClasses = classNames({
      'ps-menu-list': true,
      [`ps-menu-list-${position}`]: true,
      'ps-menu-list--hidden': !isExpanded,
    });

    return (
      <div
        className={menuClasses}
        onKeyDown={handleKeyDown}
        onClick={handleMenuItemSelection}
        ref={ref}
        {...spreadProps}
      >
        <button
          aria-controls={uniqueMenuId}
          aria-haspopup="menu"
          aria-label={buttonAriaLabel}
          aria-expanded={isExpanded ? 'true' : 'false'}
          className={buttonClasses}
          id={uniqueMenuTriggerId}
          disabled={isDisabled}
          onClick={toggleMenu}
          type="button"
          ref={buttonRef}
        >
          {isExpanded ? (
            <svg
              className="ps-menu-trigger-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.21 7.09004L17.41 6.29004L12.5 11.3L7.59004 6.29004L6.79004 7.09004L11.8 12L6.79004 16.91L7.59004 17.71L12.5 12.7L17.41 17.71L18.21 16.91L13.2 12L18.21 7.09004Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg
              className="ps-menu-trigger-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.19999 12.045C7.19999 12.93 6.47999 13.65 5.59499 13.65C4.70999 13.65 3.98999 12.93 3.98999 12.045C3.98999 11.16 4.70999 10.44 5.59499 10.44C6.47999 10.44 7.19999 11.16 7.19999 12.045ZM13.605 12.045C13.605 12.93 12.885 13.65 12 13.65C11.115 13.65 10.395 12.93 10.395 12.045C10.395 11.16 11.115 10.44 12 10.44C12.885 10.44 13.605 11.16 13.605 12.045ZM18.405 13.65C19.29 13.65 20.01 12.93 20.01 12.045C20.01 11.16 19.29 10.44 18.405 10.44C17.52 10.44 16.8 11.16 16.8 12.045C16.8 12.93 17.52 13.65 18.405 13.65Z"
                fill="currentColor"
              />
            </svg>
          )}
          {buttonText.length > 0 ? buttonText : ''}
        </button>
        <div
          className={menuListClasses}
          ref={menuContainerRef}
        >
          <div
            aria-labelledby={uniqueMenuTriggerId}
            id={uniqueMenuId}
            ref={menuRef}
            role="menu"
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
